import {
  Button,
  icons,
  Popover,
  stringify,
  useCopyToClipboard,
} from '@portal/components'
import React, { useState } from 'react'

import { useDQLEditorContext } from '../dqlEditor/context'
import { valueRenderer } from './utils'

const { Copy, Check } = icons

/**
 * A component that renders a button that, when clicked, will display a popover
 * with all of the warnings that were returned from the current DQL query response.
 * If there are no warnings, the button will not be rendered at all.
 */
export default function QueryResultWarningsPopover() {
  const [isOpen, setIsOpen] = useState(false)

  const { result: { warnings = [], totalWarningsCount = 0 } = {} } =
    useDQLEditorContext()

  const [doCopy, recentlyCopied] = useCopyToClipboard(stringify(warnings))

  const renderWarnings = () => {
    if (warnings.length === 0) {
      return null
    }

    return warnings.map((warning, index) => {
      const { _id, description } = warning

      return (
        <div
          key={index}
          className="group flex w-full gap-2 py-2 font-plex-mono"
        >
          <span>{index + 1}.</span>
          <div className="flex w-full flex-col gap-y-1.5">
            <span>{description}</span>
            {!!_id && (
              <pre className="w-[294px] overflow-x-scroll rounded-sm bg-gray-200 p-1 text-sm scrollbar-hide dark:bg-gray-900/50">
                {valueRenderer({ _id }, true)}
              </pre>
            )}
          </div>
        </div>
      )
    })
  }

  if (totalWarningsCount === 0) {
    return null
  }

  return (
    <div className="relative" data-testid="queryResultWarningsPopover">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            className="inline-flex h-6 cursor-pointer items-center rounded-sm bg-sunset-50 px-1.5 py-0.5 text-sm font-medium text-sunset outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sunset-300 dark:bg-sunset-400 dark:text-sunset-50"
            data-testid="queryResultWarningsPopoverButton"
          >
            <span>Warnings</span>
            <span className="ml-1 flex h-5 min-w-[24px] items-center justify-center rounded-sm bg-sunset px-0.5 text-white dark:bg-sunset-100 dark:text-sunset">
              {totalWarningsCount}
            </span>
          </button>
        </Popover.Trigger>

        <Popover.Content
          className="z-50 h-[400px] w-[350px] overflow-hidden rounded-sm border border-gray-100/75 bg-white shadow-sm dark:border-gray-700/50 dark:bg-gray-800"
          side="bottom"
          sideOffset={5}
          align="start"
        >
          <div className="flex h-12 shrink-0 items-center justify-between">
            <h3 className="text-base font-medium text-black dark:text-gray-50">
              Exposed warnings{' '}
              <span className="text-sunset">({warnings.length})</span>
            </h3>

            <Button
              size="icon"
              onClick={() => doCopy()}
              title="Copy warnings"
              data-testid="queryResultWarningsPopoverCopyButton"
            >
              {recentlyCopied ? (
                <Check className="h-4 w-4 text-fill-success" />
              ) : (
                <Copy className="h-4 w-4 text-foreground-subtle hover:text-foreground-normal group-focus-visible:text-foreground-normal" />
              )}
            </Button>
          </div>
          {/* NOTE: 352 = 400h body - 48header */}
          <div className="flex h-[352px] w-full flex-col gap-1 divide-y divide-gray-100 overflow-y-scroll border-y border-gray-100 scrollbar-hide dark:divide-gray-700/60 dark:border-gray-700/60">
            {renderWarnings()}
          </div>
        </Popover.Content>
      </Popover>
    </div>
  )
}
